import { useState, useEffect } from "react";
import {
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    FormControl,
    InputLabel,
    IconButton,
    Collapse
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const getRoleKey = (role: any) => {
    const roleName = role?.name ?? role?.Name ?? '';
    return roleName.charAt(0).toLowerCase() + roleName.slice(1);
};

const getPermissions = (roles: any[]) => roles.map((role: any) => ({
    roleID: role?.id ?? role?.roleID ?? role?.RoleID,
    key: getRoleKey(role),
    label: role?.name ?? role?.Name ?? ''
}));

const withRoleMappings = (client: any, permissions: any[]) => ({
    ...client,
    customerClientRoleMappings: permissions
        .filter((permission) => client[permission.key])
        .map((permission) => ({ roleID: permission.roleID }))
});

const MultiSelectDropdown = ({ data, roles, setSelectedValues }: any) => {

    const [clientUserMaps, setClientUserMaps] = useState([]);
    const [expandedClients, setExpandedClients] = useState<Set<any>>(new Set());
    const permissions = getPermissions(roles || []);

    useEffect(() => {
        setSelectedValues(clientUserMaps.map((client) => withRoleMappings(client, permissions)));
    }, [clientUserMaps, roles])

    const handleParentSelection = (parent) => {
        setClientUserMaps((prevSelected) =>
            prevSelected.some((c) => c.clientID === parent.clientID)
                ? prevSelected.filter((c) => c.clientID !== parent.clientID)
                : [...prevSelected, {
                    id: parent.id,
                    userID: parent.userID,
                    clientID: parent.clientID,
                    clientName: parent.clientName,
                    ...permissions.reduce((values, permission) => ({ ...values, [permission.key]: false }), {})
                }]
        );
    };

    const handlePermissionChange = (clientID, key) => {
        setClientUserMaps((prev) =>
            prev.map((item) =>
                item.clientID === clientID ? { ...item, [key]: !item[key] } : item
            )
        );
    };

    const toggleExpanded = (clientID: any) => {
        setExpandedClients((previous) => {
            const next = new Set(previous);
            next.has(clientID) ? next.delete(clientID) : next.add(clientID);
            return next;
        });
    };

    const selectedValues = clientUserMaps.map((client) => client.clientID);

    return (
        <FormControl fullWidth key={JSON.stringify(data)}>
            <InputLabel sx={{ marginTop: '-5px', fontSize: '12px' }}> Client Name</InputLabel>
            <Select
                multiple
                size="small"
                value={selectedValues}
                style={{ fontSize: '12px' }}
                renderValue={(selected) =>
                    selected
                        .map((id) => data?.find((opt) => opt.clientID === id)?.clientName)
                        .filter(Boolean)
                        .join(", ")
                }
            >
                {data?.map((parent) => {
                    const isParentSelected = clientUserMaps.some((c) => c.clientID === parent.clientID);
                    const parentObj = clientUserMaps.find((c) => c.clientID === parent.clientID);
                    return (
                        <div key={parent.id}>
                            <MenuItem sx={{
                                '.MuiTypography-root': {
                                    fontSize: '12px'
                                }
                            }} onClick={() => handleParentSelection(parent)}>
                                <Checkbox checked={isParentSelected} />
                                <ListItemText primary={parent.clientName} />
                                {isParentSelected && (
                                    <IconButton
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            toggleExpanded(parent.clientID);
                                        }}
                                    >
                                        <ExpandMoreIcon
                                            style={{
                                                transform: expandedClients.has(parent.clientID) ? "rotate(180deg)" : "rotate(0deg)",
                                                transition: "transform 0.2s"
                                            }}
                                        />
                                    </IconButton>
                                )}
                            </MenuItem>
                            {isParentSelected && (
                                <Collapse in={expandedClients.has(parent.clientID)} onClick={(event) => event.stopPropagation()}>
                                    <div style={{ paddingLeft: "40px" }}>
                                        {permissions.map((permission) => (
                                            <MenuItem
                                                key={permission.key}
                                                sx={{
                                                    '.MuiTypography-root': {
                                                        fontSize: '12px'
                                                    },
                                                    padding: '0'
                                                }}
                                                disableRipple
                                            >
                                                <Checkbox
                                                    checked={parentObj?.[permission.key] || false}
                                                    onChange={() => handlePermissionChange(parent.clientID, permission.key)}
                                                />
                                                <ListItemText primary={permission.label} />
                                            </MenuItem>
                                        ))}
                                    </div>
                                </Collapse>
                            )}
                        </div>
                    );
                })}
            </Select>
        </FormControl >
    );
};

export default MultiSelectDropdown;